// ============================================================================
// Block types — what kind of block a bracket is
// ============================================================================
// A bracket used to say one thing: repeat these steps N times. `SuperSet` was
// the only qualifier, and it was spelled directly into four regexes and two
// writers. Adding EMOM and AMRAP that way would have spelled eight tokens into
// the same six places, so the vocabulary lives here and everything else is
// derived from it.
//
// `superset_number` keeps its job: it tags the SIBLING GROUPS that make up one
// block. `block_type` is orthogonal — it says what kind of block those siblings
// form. A null `block_type` with a `superset_number` set is a superset, which
// is what every workout written before this existed looks like.

/** The canonical stored value. Null on a plain `Nx [` repeat. */
export type BlockType =
  | 'superset'
  | 'circuit'
  | 'complex'
  | 'emom'
  | 'amrap'
  | 'for_time'
  | 'drop_set'
  | 'rest_pause'

/** How a block's seconds parameter is read, when it has one.
 *  `interval` restarts the clock each round (EMOM); `cap` bounds the whole
 *  block (AMRAP, For Time). Both land in `block_param_seconds`; they differ in
 *  what they mean, not in how they are stored. */
export type BlockParam = 'interval' | 'cap'

interface BlockSpec {
  /** The word written in the notation. Case is the coach's business. */
  token: string
  /** Whether the seconds parameter is required, optional, or absent. */
  param: BlockParam | null
  /** Whether omitting the parameter is legal (EMOM defaults to a minute). */
  paramOptional: boolean
  /** Whether a round count precedes the token. AMRAP has none: its rounds are
   *  a RESULT, not a prescription, so `3x AMRAP` would be a contradiction. */
  counted: boolean
  /** Whether the block spans SIBLING groups, one per exercise, tagged with a
   *  shared `superset_number`. A drop set and a rest-pause set are one
   *  exercise by definition, so giving them a superset number would make the
   *  grid draw a single card as a superset, and the round trip would not
   *  match the tree it started from. */
  siblings: boolean
}

export const BLOCK_SPECS: Record<BlockType, BlockSpec> = {
  superset:   { token: 'SuperSet',  param: null,       paramOptional: false, counted: true, siblings: true },
  circuit:    { token: 'Circuit',   param: null,       paramOptional: false, counted: true, siblings: true },
  complex:    { token: 'Complex',   param: null,       paramOptional: false, counted: true, siblings: true },
  emom:       { token: 'EMOM',      param: 'interval', paramOptional: true,  counted: true, siblings: true },
  amrap:      { token: 'AMRAP',     param: 'cap',      paramOptional: false, counted: false, siblings: true },
  for_time:   { token: 'ForTime',   param: 'cap',      paramOptional: true,  counted: true, siblings: true },
  drop_set:   { token: 'DropSet',   param: null,       paramOptional: false, counted: true, siblings: false },
  rest_pause: { token: 'RestPause', param: null,       paramOptional: false, counted: true, siblings: false },
}

/** An EMOM with no interval written is every minute. Spelled once, here,
 *  because the writer omits it and the reader has to put it back. */
export const EMOM_DEFAULT_INTERVAL_SECONDS = 60

const BY_TOKEN: Record<string, BlockType> = Object.fromEntries(
  (Object.keys(BLOCK_SPECS) as BlockType[]).map((t) => [BLOCK_SPECS[t].token.toLowerCase(), t]),
)

/** The token a coach typed, in any case, to the stored value. */
export function blockTypeFromToken(token: string | undefined | null): BlockType | null {
  if (!token) return null
  return BY_TOKEN[token.trim().toLowerCase()] ?? null
}

/** The token to write for a stored value. */
export function tokenForBlockType(type: BlockType | null | undefined): string | null {
  return type ? BLOCK_SPECS[type].token : null
}

/** Alternation of every token, longest first so `ForTime` cannot be shadowed by
 *  a shorter prefix, for embedding in the bracket-header patterns. */
export const BLOCK_TOKEN_ALT = (Object.keys(BLOCK_SPECS) as BlockType[])
  .map((t) => BLOCK_SPECS[t].token)
  .sort((a, b) => b.length - a.length)
  .join('|')

/** `m:ss`, the only shape a block parameter takes. Deliberately NOT the target
 *  clock pattern: a block parameter is whole seconds (a 90-second EMOM, a 20
 *  minute cap), never tenths, and accepting tenths here would let `1:10.4` read
 *  as an EMOM interval when it is a lap time. */
export const BLOCK_PARAM_RE = /^(\d+):([0-5]\d)$/

export function parseBlockParam(raw: string | undefined | null): number | null {
  if (!raw) return null
  const m = raw.trim().match(BLOCK_PARAM_RE)
  if (!m) return null
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10)
}

export function formatBlockParam(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** The seconds a writer should WRITE for a block, or null to write nothing.
 *  An EMOM at the default interval writes no parameter, the same rule the
 *  lap-time writer uses for a zero tenth: the shortest form that reads back
 *  identically is the canonical one. */
export function blockParamToWrite(
  type: BlockType | null | undefined,
  seconds: number | null | undefined,
): number | null {
  if (!type || seconds == null) return null
  const spec = BLOCK_SPECS[type]
  if (!spec.param) return null
  if (type === 'emom' && seconds === EMOM_DEFAULT_INTERVAL_SECONDS) return null
  return seconds
}
